import { cn } from "@/lib/utils";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import * as React from "react";

const navigationMenuItemStyle = cva(
    "flex-1 border-b-2 border-white hover:border-primary-dark/60 h-9 items-center justify-center bg-background py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-accent-foreground data-[state=active]:bg-accent/50 data-[state=active]:hover:bg-accent data-[state=active]:focus:bg-accent",
    {
        variants: {
            state: {
                active: "border-primary-dark/60",
                inactive: "",
            },
            variant: {
                default: "",
                deepFocus:
                    "border-white/20 bg-transparent text-white hover:border-white/40 data-[state=active]:border-white/50 data-[state=active]:bg-white/10 data-[state=active]:text-white",
            },
        },
        defaultVariants: {
            state: "inactive",
            variant: "default",
        },
    },
);

const NavigationMenu = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, children, ...props }, ref) => (
    <NavigationMenuPrimitive.Root
        ref={ref}
        className={cn(
            "flex w-full items-center overflow-hidden [&_>div]:w-full",
            className,
        )}
        {...props}
    >
        <NavigationMenuPrimitive.List className="flex list-none items-center [&_li]:flex-1">
            {children}
        </NavigationMenuPrimitive.List>
    </NavigationMenuPrimitive.Root>
));
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

const NavigationMenuItem = ({
    value,
    label,
    count,
    onSelect,
    activeValue,
    variant = "default",
}: {
    value: string;
    label: React.ReactNode;
    count: number | string;
    onSelect: (value: string) => void;
    activeValue?: string;
    variant?: "default" | "deepFocus";
}) => {
    const isActive = value === activeValue;

    return (
        <NavigationMenuPrimitive.Item value={value}>
            <button
                aria-controls={`panel-${value}`}
                aria-selected={isActive}
                className={cn(
                    navigationMenuItemStyle({
                        state: isActive ? "active" : "inactive",
                        variant,
                    }),
                    "flex w-full gap-2 px-4",
                )}
                id={`tab-${value}`}
                onClick={() => onSelect(value)}
                role="tab"
                type="button"
            >
                <p
                    className={cn(
                        "truncate",
                        variant === "deepFocus" && "text-white",
                    )}
                >
                    {label}
                </p>
                <p
                    className={cn(
                        "text-xs text-text-caption",
                        variant === "deepFocus" && "text-white/70",
                    )}
                >
                    {count}
                </p>
            </button>
        </NavigationMenuPrimitive.Item>
    );
};

export { NavigationMenu, NavigationMenuItem };
